import { UserModel } from '$tablet-app/src/app/auth/UserModel';
import { getHours } from '$tablet-app/src/app/utils/dateTime';
import { randomIntFromInterval } from '$tablet-app/src/app/utils/randomisation';
import { User } from '@akin-home/amplify';

import {
  avatar_note_content,
  createStickyNote,
  StickyNote,
} from '@akin-home/sticky-notes';

import {
  serializeStickyNoteForPost,
  serializeStickyNoteForPut,
  stickyNoteAcknowledgementGraphqlToModel,
  stickyNoteGraphqlToModel,
} from '@akin-home/sticky-notes/frontend';

export {
  createStickyNote,
  stickyNoteAcknowledgementGraphqlToModel,
  stickyNoteGraphqlToModel,
  serializeStickyNoteForPost,
  serializeStickyNoteForPut,
};

export const getStickyNoteUserAcknowledgement = (
  note: StickyNote,
  user_id: string
) => note.acknowledgements?.find((ack) => ack.user_id === user_id);

export const userHasAcknowledgedStickyNote = (
  note: StickyNote,
  user_id: string
) => !!note.acknowledgements?.some((ack) => ack.user_id === user_id);

/** Sets the acknowledment of a sticky note */
export const setStickyNoteAcknowledgement = (
  note: StickyNote,
  user_id: string
) => {
  const acknowledgements = note.acknowledgements || [];

  if (userHasAcknowledgedStickyNote(note, user_id)) {
    return {
      ...note,
      acknowledgements: acknowledgements.filter(
        (ack) => ack.user_id !== user_id
      ),
    };
  } else {
    const new_acknowledgement = { user_id, date: new Date().toISOString() };

    return {
      ...note,
      acknowledgements: [...acknowledgements, new_acknowledgement],
    };
  }
};

export const toggleStickyNoteSharedWithIDs = (
  note: StickyNote,
  user_id: string
): StickyNote => {
  const sharedWithIDs = note.sharedWithIDs || ([] as string[]);

  return {
    ...note,
    sharedWithIDs: sharedWithIDs.includes(user_id)
      ? sharedWithIDs.filter((id) => id !== user_id)
      : [...sharedWithIDs, user_id],
  };
};

/** Gets the font style for a sticky note. Depends on the length of the notes content. */
export const getStickyNoteFontStyle = (note: StickyNote) => {
  const { content } = note;

  if (!content) {
    return;
  }

  if (content.length <= 35) {
    return {
      fontSize: 48,
      lineHeight: 65,
    };
  } else if (content.length <= 50) {
    return {
      fontSize: 40,
      lineHeight: 50,
    };
  } else if (content.length <= 65) {
    return {
      fontSize: 35,
      lineHeight: 45,
    };
  } else if (content.length <= 80) {
    return {
      fontSize: 30,
      lineHeight: 40,
    };
  } else if (content.length <= 100) {
    return {
      fontSize: 24,
      lineHeight: 30,
    };
  }

  return {
    fontSize: 48,
    lineHeight: 58,
  };
};

export const filterUsersSharedForStickyNote = (
  note: StickyNote,
  users?: User[]
): User[] =>
  users ? users.filter((u) => note.sharedWithIDs?.includes(u.id)) : [];

export const checkUserIsNoteOwner = (
  note: StickyNote,
  user: UserModel
): boolean => note.owner?.id === user.id;

export const noteIsForEveryone = (note: StickyNote): boolean =>
  !note.sharedWithIDs || note.sharedWithIDs.length === 0;

/** Returns if the given user can view the given sticky note */
export const userCanViewStickyNote =
  (user: UserModel) =>
  (note: StickyNote): boolean => {
    return (
      noteIsForEveryone(note) ||
      checkUserIsNoteOwner(note, user) ||
      !!note.sharedWithIDs?.includes(user.id)
    );
  };

export const filterStickyNotesForUser = (
  notes: StickyNote[],
  user: UserModel
): StickyNote[] => notes.filter(userCanViewStickyNote(user));

export const getAvatarStickyNoteTimePeriod = (
  date: Date
): 'morning' | 'noon' | 'evening' => {
  const hour = getHours(date);

  if (hour <= 12) {
    return 'morning';
  } else if (hour > 12 && hour < 17) {
    return 'noon';
  } else {
    return 'evening';
  }
};

/** Computes sticky note content from the avatar for the given time of day */
export const getAvatarStickyNote = (
  time_of_day: 'morning' | 'noon' | 'evening'
): string => {
  const content_options = avatar_note_content[time_of_day];

  return content_options[randomIntFromInterval(0, content_options.length)];
};
